import { activeSiteConfig } from "@config/site/site-config";
import {
    CheckoutStep,
    type THeapPaymentMethodsJson,
    type THeapPaymentMethodsResponse,
} from "@features/checkout/types";
import { useCheckoutErrorState } from "@features/checkout/utils/useCheckoutErrorState";
import type { Money } from "@graphql/generated/types";
import { ensureActiveSession } from "@lib/auth/ensureActiveSession";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import { getPagePathByLocale, orderConfirmationPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useApi } from "@lib/helpers/useApi";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { validateRecaptcha } from "@lib/utils/validateRecaptcha";
import { useStore, useStoreApi } from "@store/index";
import { Text } from "@ecco/ui/text";
import { Money as MoneyComponent } from "@ecco/ui/money";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { useRouter } from "@i18n/routing";
import { useAuth } from "@lib/auth/useAuth";
import { useLocale } from "next-intl";
import { useEffect, useState, type ReactNode } from "react";
import type { StepFormProps } from "../StepForm";
import StepForm from "../StepForm";
import { processPayment } from "./actions/processPayment";
import { getSessionIDForKount } from "@lib/utils/kount/session";
import {
    fetchHeapPaymentMethods,
    findNoPayMethodName,
} from "@lib/utils/payment-methods/fetchHeapPaymentMethods";
import { trackEvent } from "@components/ab-test/wingify/event";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { RECAPTCHA_CONTEXT } from "@lib/utils/recaptcha/types";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

export type HeapPaymentStepWithTotalZeroFormProps = StepFormProps & {
    currentCartTotalPrice: Money;
    recaptchaAgreement?: ReactNode;
    cartId: string;
};

export const HeapPaymentStepWithTotalZeroForm = ({
    currentCartTotalPrice,
    title,
    recaptchaAgreement,
    cartId,
    isExpandedId,
    stepId,
    ...stepFormProps
}: HeapPaymentStepWithTotalZeroFormProps) => {
    const locale = useLocale();
    const getCart = useStore((state) => state.getCart);
    const { shippingInfo } = getCart();
    const { session } = useAuth();
    const { push } = useRouter();

    const api = useStoreApi();
    const cart = api.getState().data.me.cartInfo.cart;
    const getUtmParams = useStore((state) => state.getUtmParams);

    const { setStepError, clearStepError } = useCheckoutErrorState();

    const [isLoading, setIsLoading] = useState(false);

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.page, [
        Checkout.step3.zeroAmountMessage,
        Checkout.step3.zeroAmountTotal,
        Checkout.step3.zeroAmountButton,
        Checkout.step3.zeroAmountErrorMessage,
    ]);

    const { data: paymentMethodsResponse } = useApi.useAsync<THeapPaymentMethodsResponse>(
        [QueryKeys.getPaymentMethods, "heap", shippingInfo?.shippingMethodName, cart.id],
        async () => {
            await ensureActiveSession(session);
            const result = await fetchHeapPaymentMethods(cart.id);
            const paymentMethodsJson = JSON.parse(
                result.paymentMethodsJson
            ) as THeapPaymentMethodsJson[];
            return {
                ...result,
                parsedPaymentMethodsJson: paymentMethodsJson,
            };
        },
        {
            enabled: !!cart.id && isExpandedId === stepId && !!session,
            staleTime: 0,
            meta: {
                serviceType: ServiceType.ADYEN,
                message: `HEAP PAYMENT: Issue loading payment methods for cartId: ${cartId}`,
                tag: LogTag.PAYMENT,
                payload: { cartId },
            },
        }
    );

    const noPayMethodName = findNoPayMethodName(paymentMethodsResponse?.parsedPaymentMethodsJson);

    function showErrorMessage(error?: unknown) {
        setIsLoading(false);
        setStepError(CheckoutStep.PAYMENT, microcopies[Checkout.step3.zeroAmountErrorMessage]);

        Logger.error(
            ServiceType.ADYEN,
            `PAYMENT: Issue with order creation for cartId: ${cartId}`,
            {
                tag: LogTag.PAYMENT,
                cartId,
                error,
            }
        );
    }

    async function validateRecaptchaAndOrderCreation() {
        clearStepError(CheckoutStep.PAYMENT);
        setIsLoading(true);
        try {
            await validateRecaptcha({
                shouldCheck: activeSiteConfig.reCaptchaIntegration.checkout,
                action: "PLACE_ZERO_AMOUNT_ORDER",
                onSuccess: async () => {
                    try {
                        if (!noPayMethodName) {
                            showErrorMessage(new Error("No NoPay payment method available"));
                            return;
                        }

                        const response = await processPayment({
                            cartId,
                            paymentMethod: noPayMethodName,
                            utm: getUtmParams(),
                            locale,
                            providerType: "NoPay",
                            kountSessionId: getSessionIDForKount(),
                        });

                        trackEvent(`Zero Payment`, cart);

                        if (
                            !(
                                response?.isPaymentComplete ||
                                response?.isPaymentPending ||
                                response?.orderNumber
                            )
                        ) {
                            showErrorMessage(
                                new Error(
                                    `NoPay order not completed: resultCode=${response.resultCode}`
                                )
                            );
                            return;
                        }
                        // Prefer orderDraftId so the confirmation page resolves this exact
                        // attempt; retries after a failure can share an orderNumber.
                        const confirmationId = response.orderDraftId || response.orderNumber;

                        if (!confirmationId) {
                            showErrorMessage(
                                new Error(
                                    `NoPay order missing identifier: resultCode=${response.resultCode}`
                                )
                            );
                            return;
                        }

                        if (response.orderNumber) {
                            // Not awaited: analytics must never hold back the confirmation redirect.
                            void AnalyticsClient.purchaseAdded(
                                { ...getCart(), orderNumber: response.orderNumber },
                                noPayMethodName
                            );
                        }

                        push(
                            `${getPagePathByLocale(orderConfirmationPage, locale)}/v2/${confirmationId}`
                        );
                    } catch (error) {
                        showErrorMessage(error);
                    }
                },
                onAbort: () => {
                    showErrorMessage(new Error(RECAPTCHA_CONTEXT.FAILED_VALIDATION));
                },
                onError: (error) => {
                    showErrorMessage(error);
                },
            });
        } catch (error) {
            showErrorMessage(error);
        }
    }

    useEffect(() => {
        if (isExpandedId !== CheckoutStep.PAYMENT) {
            clearStepError(CheckoutStep.PAYMENT);
            setIsLoading(false);
        }
    }, [isExpandedId]);

    return (
        <StepForm
            {...stepFormProps}
            title={title}
            stepId={stepId}
            isExpandedId={isExpandedId}
            className="payment-step-form"
        >
            <Box display="flex" flexDirection="column" gap={6}>
                <Text fontWeight={"bold"} fontSize={"normal"} color={"gray.500"}>
                    {microcopies[Checkout.step3.zeroAmountMessage]}
                </Text>

                <Text fontWeight={"bold"}>
                    {microcopies[Checkout.step3.zeroAmountTotal]}:{" "}
                    <MoneyComponent
                        as={"span"}
                        data={currentCartTotalPrice}
                        locale={locale}
                        data-testid="summarymoney"
                    />
                </Text>

                <Button
                    onClick={validateRecaptchaAndOrderCreation}
                    width={[36, 40]}
                    isLoading={isLoading}
                    data-dd-action-name={RUM_ACTION_NAME.PLACE_ORDER_ZERO_TOTAL}
                >
                    {microcopies[Checkout.step3.zeroAmountButton]}
                </Button>
            </Box>
            {recaptchaAgreement && activeSiteConfig.reCaptchaIntegration.checkout && (
                <Box pt={6} w="100%" maxW={75}>
                    {recaptchaAgreement}
                </Box>
            )}
        </StepForm>
    );
};
